import { useState } from 'react';
import type { Question, Option } from './types';

type Props = {
  question: Question;
  options: Option[];
  onChange: (patch: Partial<Question>) => void;
  onChangeOption: (idx: number, patch: Partial<Option>) => void;
  onClose: () => void;
};

/**
 * 高级设置 modal：折叠 quota / requiredRead / 各选项的 quota + onQuotaFull
 * 用户在主 UI 只看到题干 + 选项 + 必填；高级点 ⋮ 菜单打开
 */
export default function AdvancedModal({ question, options, onChange, onChangeOption, onClose }: Props) {
  const [tab, setTab] = useState<'base' | 'quota'>('base');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-advanced" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>高级设置</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">✕</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${tab === 'base' ? 'is-active' : ''}`}
            onClick={() => setTab('base')}
          >
            基础
          </button>
          {(question.type === 'single' || question.type === 'multi') && (
            <button
              className={`tab-btn ${tab === 'quota' ? 'is-active' : ''}`}
              onClick={() => setTab('quota')}
            >
              选项配额
            </button>
          )}
        </div>

        <div className="modal-body">
          {tab === 'base' && (
            <>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={question.requiredRead ?? false}
                  onChange={(e) => onChange({ requiredRead: e.target.checked })}
                />
                必读确认（签收场景）
              </label>
              <div className="hint">
                必读确认：受访者必须勾选后才提交，独立于"必填"。
              </div>
              {(question.type === 'text' || question.type === 'longtext') && (
                <>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean((question.params as { allowImageUpload?: boolean } | undefined)?.allowImageUpload)}
                      onChange={(e) => onChange({
                        params: {
                          ...((question.params ?? {}) as Record<string, unknown>),
                          allowImageUpload: e.target.checked,
                        },
                      })}
                    />
                    允许填写者上传图片（最多 3 张）
                  </label>
                  <div className="hint">单张图片不超过 1.5MB，图片以 data URL 保存。</div>
                </>
              )}
            </>
          )}

          {tab === 'quota' && (
            <div className="advanced-quota-list">
              <div className="hint">为每个选项设置配额 + 满额后行为。</div>
              {options.map((o, i) => (
                <div key={i} className="advanced-quota-row">
                  <div className="advanced-quota-value">「{o.value}」</div>
                  <label className="input-line">
                    配额
                    <input
                      type="number"
                      className="input"
                      style={{ width: 90 }}
                      min={0}
                      value={o.quota ?? ''}
                      placeholder="不限"
                      onChange={(e) =>
                        onChangeOption(i, {
                          quota: e.target.value === '' ? undefined : Number(e.target.value),
                        })
                      }
                    />
                  </label>
                  <label className="input-line">
                    满后
                    <select
                      className="input"
                      value={o.onQuotaFull ?? 'jumpNext'}
                      onChange={(e) => onChangeOption(i, { onQuotaFull: e.target.value as 'jumpNext' | 'end' })}
                    >
                      <option value="jumpNext">跳下题</option>
                      <option value="end">终止问卷</option>
                    </select>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn btn-primary" onClick={onClose}>完成</button>
        </div>
      </div>
    </div>
  );
}
